import { CreditCard, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useBillingActions } from "@/hooks/queries/use-billing";
import { useOnboardingActions } from "@/hooks/queries/use-onboarding";
import type { SubscriptionState } from "@/lib/api/billing";

/** What the organization's plan is, in one line, and what an admin can do next. */
function describePlan(s: SubscriptionState): { title: string; detail: string } {
  const price = `$${String(s.pricing.teamPricePerSeatUsd)} per person on a team, per month`;
  switch (s.plan) {
    case "free":
      return {
        title: "Free",
        detail: `${String(s.people)} of ${String(s.pricing.freePeopleLimit)} people. Upgrade to Team to add more (${price}).`,
      };
    case "team":
      if (s.hasSubscription) {
        return { title: "Team", detail: `${price}. ${String(s.seatQuantity)} paid seat${s.seatQuantity === 1 ? "" : "s"}.` };
      }
      if (s.trialOver) {
        return {
          title: "Team — trial ended",
          detail: "Your organization is read-only until an admin adds payment details. Your work is safe.",
        };
      }
      return {
        title: `Team — free trial, ${String(s.trialDaysLeft ?? 0)} day${s.trialDaysLeft === 1 ? "" : "s"} left`,
        detail: `Add payment details before the trial ends to keep working. Then ${price}.`,
      };
    case "enterprise":
      return { title: "Enterprise", detail: "Billed by contract. Contact support@mycompbox.com with questions." };
    default:
      return { title: "Free (early access)", detail: "Your organization joined before plans existed. No limits." };
  }
}

/**
 * The organization's plan on the Billing page, with the payment actions an
 * org admin can take (Stripe Checkout or the Stripe billing portal) and the
 * Managed AI waitlist.
 */
export function PlanCard({ subscription, isAdmin }: { subscription: SubscriptionState; isAdmin: boolean }) {
  const { checkout, portal } = useBillingActions();
  const { joinManagedAiWaitlist } = useOnboardingActions();
  const { title, detail } = describePlan(subscription);
  const busy = checkout.isPending || portal.isPending;
  const error = (checkout.error ?? portal.error) instanceof Error ? (checkout.error ?? portal.error)?.message : null;

  let action: { label: string; run: () => void } | null = null;
  if (subscription.subscriptionStatus === "canceled" || subscription.subscriptionStatus === "incomplete") {
    action = { label: "Subscribe to Team", run: () => { checkout.mutate(); } };
  } else if (subscription.hasSubscription) {
    action = { label: "Manage billing", run: () => { portal.mutate(); } };
  } else if (subscription.plan === "team") {
    action = { label: "Add payment details", run: () => { checkout.mutate(); } };
  } else if (subscription.plan === "free") {
    action = { label: "Upgrade to Team", run: () => { checkout.mutate(); } };
  }

  return (
    <Card data-testid="plan-card">
      <CardHeader>
        <CardTitle>
          Plan: <span data-testid="plan-title">{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{detail}</p>

        {action &&
          (isAdmin ? (
            subscription.checkoutAvailable ? (
              <Button disabled={busy} onClick={action.run}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                {action.label}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                Online payment isn&apos;t set up yet. Email support@mycompbox.com and we&apos;ll set up your plan.
              </p>
            )
          ) : (
            <p className="text-xs text-muted-foreground">Only organization admins can change the plan.</p>
          ))}
        {error && (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border p-3 text-sm">
          <span>
            <span className="font-medium">Managed AI</span>{" "}
            <span className="text-muted-foreground">
              · coming soon — we provide the AI, so nobody needs their own account.
            </span>
          </span>
          {subscription.managedAiWaitlisted || joinManagedAiWaitlist.isSuccess ? (
            <span className="text-muted-foreground">You&apos;re on the waitlist ✓</span>
          ) : (
            isAdmin && (
              <Button
                variant="outline"
                size="sm"
                disabled={joinManagedAiWaitlist.isPending}
                onClick={() => {
                  joinManagedAiWaitlist.mutate();
                }}
              >
                Join the waitlist
              </Button>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}
